import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  trainingCertificatesApi,
  trainingTypesApi,
  personnelApi,
  pdvApi,
  ocrApi,
  personnelReportsApi as reportsApi,
} from '../lib/api';
import { toast } from 'sonner';
import {
  CalendarClock,
  Upload,
  Download,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Filter,
  Mail,
  Search,
  FileText,
  Plus,
  Sparkles,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';

const statusBadge = (status, days) => {
  if (status === 'expired')
    return <Badge className="bg-red-100 text-red-800 border border-red-300">🔴 Scaduto {days != null && `(${Math.abs(days)}g fa)`}</Badge>;
  if (status === 'expiring_soon')
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-300">🟡 Scade tra {days}g</Badge>;
  if (status === 'no_expiry')
    return <Badge className="bg-gray-100 text-gray-700 border">⚪ Senza scadenza</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">🟢 Valido {days != null && `(${days}g)`}</Badge>;
};

const formatBytes = (b) => {
  if (!b) return '-';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const initialForm = {
  personnel_id: '',
  training_type_id: '',
  issue_date: new Date().toISOString().split('T')[0],
  expiry_date: '',
  duration_hours: '',
  issuer: '',
  notes: '',
  file: null,
};

export default function TrainingCertificatesPage() {
  const [certs, setCerts] = useState([]);
  const [types, setTypes] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pdvFilter, setPdvFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [sendingNotif, setSendingNotif] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const [certsRes, typesRes, pRes, pdvRes] = await Promise.all([
        trainingCertificatesApi.getAll(),
        trainingTypesApi.getAll(),
        personnelApi.getAll().catch(() => ({ data: [] })),
        pdvApi.getAll().catch(() => ({ data: [] })),
      ]);
      setCerts(certsRes.data);
      setTypes(typesRes.data.filter((t) => t.is_active));
      const pData = Array.isArray(pRes.data) ? pRes.data : pRes.data.items || [];
      setPersonnel(pData);
      const pdvData = Array.isArray(pdvRes.data) ? pdvRes.data : pdvRes.data.items || [];
      setPdvs(pdvData);
    } catch (e) {
      toast.error('Errore caricamento certificati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-compute expiry when user picks a type + issue date
  const selectedType = useMemo(
    () => types.find((t) => t.id === form.training_type_id),
    [types, form.training_type_id]
  );
  useEffect(() => {
    if (selectedType && form.issue_date && !form.expiry_date && selectedType.validity_years) {
      const d = new Date(form.issue_date);
      d.setFullYear(d.getFullYear() + selectedType.validity_years);
      setForm((f) => ({
        ...f,
        expiry_date: d.toISOString().split('T')[0],
        duration_hours: f.duration_hours || selectedType.default_duration_hours || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.training_type_id, form.issue_date]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return certs.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (pdvFilter !== 'all' && c.pdv_id !== pdvFilter) return false;
      if (q) {
        const hay = `${c.personnel_name || ''} ${c.personnel_fiscal_code || ''} ${c.training_type_name || ''} ${c.pdv_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [certs, statusFilter, pdvFilter, search]);

  const stats = useMemo(() => ({
    total: certs.length,
    expired: certs.filter((c) => c.status === 'expired').length,
    expiring: certs.filter((c) => c.status === 'expiring_soon').length,
    valid: certs.filter((c) => c.status === 'valid').length,
  }), [certs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error('Seleziona un file'); return; }
    if (!form.personnel_id) { toast.error('Seleziona il dipendente'); return; }
    if (!form.training_type_id) { toast.error('Seleziona il tipo formazione'); return; }
    if (!form.issue_date) { toast.error('Inserisci la data di rilascio'); return; }
    try {
      setUploading(true);
      await trainingCertificatesApi.upload(form.file, {
        personnel_id: form.personnel_id,
        training_type_id: form.training_type_id,
        issue_date: form.issue_date,
        expiry_date: form.expiry_date || undefined,
        duration_hours: form.duration_hours || undefined,
        issuer: form.issuer || undefined,
        notes: form.notes || undefined,
      });
      toast.success('Certificato caricato');
      setShowUpload(false);
      setForm(initialForm);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const deleteCert = async (c) => {
    if (!window.confirm(`Eliminare il certificato di ${c.personnel_name}?`)) return;
    try {
      await trainingCertificatesApi.delete(c.id);
      toast.success('Certificato eliminato');
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const sendNotifications = async () => {
    if (!window.confirm('Inviare email di alert per tutte le scadenze entro 60 giorni?\nVerranno notificati admin e dipendenti con scadenze ravvicinate.')) return;
    try {
      setSendingNotif(true);
      const res = await trainingCertificatesApi.sendExpiryNotifications(60);
      toast.success(
        `Notifiche inviate: ${res.data.admin_emails_sent} admin + ${res.data.trainee_emails_sent} dipendenti (${res.data.total_alerts} scadenze)`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio notifiche');
    } finally {
      setSendingNotif(false);
    }
  };

  const personnelName = (p) => `${p.last_name || ''} ${p.first_name || ''}`.trim() || p.fiscal_code;

  // OCR: when a file is selected, ask GPT-4o vision to auto-extract fields
  const handleFileChange = async (file) => {
    setForm((f) => ({ ...f, file }));
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) return;
    try {
      setOcrLoading(true);
      const res = await ocrApi.extract(file, 'training_certificate');
      const e = res.data?.extracted || {};
      const matched = res.data?.matched_personnel;
      const matchedType = res.data?.matched_training_type;
      setForm((f) => ({
        ...f,
        personnel_id: matched?.id || f.personnel_id,
        training_type_id: matchedType?.id || f.training_type_id,
        issue_date: e.issue_date || f.issue_date,
        expiry_date: e.expiry_date || '',
        duration_hours: e.duration_hours ?? f.duration_hours,
        issuer: e.issuer || f.issuer,
      }));
      const parts = [];
      if (matched) parts.push(`dipendente ${matched.last_name} ${matched.first_name}`);
      if (matchedType) parts.push(`tipo "${matchedType.name}"`);
      if (e.issue_date) parts.push(`rilascio ${e.issue_date}`);
      if (parts.length) toast.success(`✨ Auto-compilato: ${parts.join(' · ')}`);
      else toast.info('Documento letto. Verifica e completa i campi mancanti.');
    } catch (err) {
      toast.warning("OCR automatico fallito, compila manualmente");
    } finally {
      setOcrLoading(false);
    }
  };

  const downloadReport = (format) => {
    window.open(reportsApi.getPersonnelSummaryUrl({ format }), '_blank');
  };

  return (
    <div className="space-y-6" data-testid="training-certificates-page">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarClock className="w-8 h-8 text-blue-600" />
            Scadenze Formazione
          </h1>
          <p className="text-gray-600 mt-1">
            Carica i certificati di formazione già conseguiti dai dipendenti. Il sistema calcola la scadenza e ti avvisa quando serve il rinnovo.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadReport('xlsx')} data-testid="report-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Report Excel
          </Button>
          <Button variant="outline" onClick={() => downloadReport('pdf')} data-testid="report-pdf-btn">
            <FileText className="w-4 h-4 mr-2" /> Report PDF
          </Button>
          <Button variant="outline" onClick={sendNotifications} disabled={sendingNotif} data-testid="send-notifications-btn">
            <Mail className="w-4 h-4 mr-2" />
            {sendingNotif ? 'Invio...' : 'Invia alert email'}
          </Button>
          <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-cert-btn">
            <Upload className="w-4 h-4 mr-2" /> Carica certificato
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Totale</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="py-4">
            <div className="text-xs text-emerald-700 uppercase tracking-wide flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Validi
            </div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.valid}</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="py-4">
            <div className="text-xs text-amber-700 uppercase tracking-wide flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> In scadenza (60g)
            </div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{stats.expiring}</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="py-4">
            <div className="text-xs text-red-700 uppercase tracking-wide flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Scaduti
            </div>
            <div className="text-2xl font-bold text-red-700 mt-1">{stats.expired}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Cerca</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
                <Input
                  placeholder="Nome, CF, formazione..."
                  className="pl-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Stato</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="valid">🟢 Validi</SelectItem>
                  <SelectItem value="expiring_soon">🟡 In scadenza</SelectItem>
                  <SelectItem value="expired">🔴 Scaduti</SelectItem>
                  <SelectItem value="no_expiry">⚪ Senza scadenza</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs">Unità Locale</Label>
              <Select value={pdvFilter} onValueChange={setPdvFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le UL</SelectItem>
                  {pdvs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name || p.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-500">
            <CalendarClock className="w-16 h-16 mx-auto mb-4 opacity-30" />
            {certs.length === 0
              ? "Nessun certificato caricato. Clicca 'Carica certificato' per iniziare."
              : 'Nessun certificato corrisponde ai filtri.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dipendente</TableHead>
                  <TableHead>Unità Locale</TableHead>
                  <TableHead>Formazione</TableHead>
                  <TableHead>Rilascio</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Ente</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.personnel_name || '-'}</div>
                      <div className="text-xs text-gray-500 font-mono">{c.personnel_fiscal_code || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">{c.pdv_name || '-'}</TableCell>
                    <TableCell>
                      <div className="text-sm">{c.training_type_name || '-'}</div>
                      {c.duration_hours && (
                        <div className="text-xs text-gray-500">{c.duration_hours}h</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{c.issue_date || '-'}</TableCell>
                    <TableCell className="text-sm">{c.expiry_date || 'N/D'}</TableCell>
                    <TableCell>{statusBadge(c.status, c.days_to_expiry)}</TableCell>
                    <TableCell className="text-xs text-gray-600">{c.issuer || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Scarica file"
                          onClick={() => window.open(trainingCertificatesApi.getDownloadUrl(c.id), '_blank')}
                          data-testid={`download-cert-${c.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Elimina"
                          onClick={() => deleteCert(c)}
                          data-testid={`delete-cert-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Carica certificato formazione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                File certificato * (PDF, JPG, PNG, DOCX - max 20MB)
                {ocrLoading && (
                  <span className="text-xs text-blue-600 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 animate-pulse" /> Lettura automatica in corso...
                  </span>
                )}
              </Label>
              <Input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx,.doc"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                data-testid="upload-file"
                required
              />
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-blue-600" />
                I campi sotto verranno compilati automaticamente leggendo il documento (PDF/JPG/PNG)
              </p>
            </div>
            <div className="space-y-1">
              <Label>Dipendente *</Label>
              <Select
                value={form.personnel_id}
                onValueChange={(v) => setForm({ ...form, personnel_id: v })}
              >
                <SelectTrigger data-testid="select-personnel">
                  <SelectValue placeholder="Seleziona dipendente" />
                </SelectTrigger>
                <SelectContent>
                  {personnel.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {personnelName(p)} {p.fiscal_code ? `(${p.fiscal_code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Tipo di formazione *</Label>
              <Select
                value={form.training_type_id}
                onValueChange={(v) => setForm({ ...form, training_type_id: v, expiry_date: '' })}
              >
                <SelectTrigger data-testid="select-training-type">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.validity_years ? ` (validità ${t.validity_years} anni)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType?.description && (
                <p className="text-xs text-gray-500">{selectedType.description}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data rilascio *</Label>
                <Input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value, expiry_date: '' })}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label>Data scadenza</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                  placeholder="Auto-calcolato"
                />
                <p className="text-xs text-gray-500">Auto-calcolato dal tipo. Puoi modificarla.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Ore formazione</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.duration_hours}
                  onChange={(e) => setForm({ ...form, duration_hours: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Ente formatore</Label>
                <Input
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  placeholder="es. CEN Engineering"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={uploading} data-testid="confirm-upload-cert">
                {uploading ? 'Caricamento...' : 'Carica certificato'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
